import { useMemo, useState } from 'react';
import { exportReportBlockPdf, exportReportBlockWorkbook, exportStudentPdf, exportStudentWorkbook } from '../lib/exporters.js';
import { buildReportBlock, buildStudentReport, createReportBlockConfig, parseReportBlockConfig, serializeReportBlockConfig } from '../lib/reportsEngine.js';
import { formatDate } from '../lib/utils.js';

function visibleStudents(data, user) {
  if (!user) return [];
  if (user.role === 'school_admin') return (data.students || []).filter((student) => student.schoolId === user.schoolId);
  if (user.role === 'professional') return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.profissionais?.includes(user.uid));
  return [];
}

function downloadJson(name, text) {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function StudentReportRow({ student, report, selected, onToggle, data, user }) {
  return <article className='report-student-row'>
    <label><input type='checkbox' checked={selected} onChange={() => onToggle(student.id)} /> <b>{student.nome}</b></label>
    <small>{student.serie || 'sem turma'} · média atual {report.summary.currentAverage}% · {report.alerts.length} alerta(s) · consentimento {report.consent ? 'ok' : 'pendente'}</small>
    <div className='actions'>
      <button type='button' onClick={() => exportStudentPdf(student, data)}>PDF individual</button>
      <button type='button' onClick={() => exportStudentWorkbook(student, data)}>Excel individual</button>
    </div>
  </article>;
}

export function ReportsCenter({ user, data, setData, onSave }) {
  const students = useMemo(() => visibleStudents(data, user), [data, user]);
  const reportsById = useMemo(() => Object.fromEntries(students.map((student) => [student.id, buildStudentReport(data, student)])), [students, data]);
  const [selected, setSelected] = useState(() => new Set());
  const [blockName, setBlockName] = useState('Bloco de relatórios');
  const [message, setMessage] = useState('');
  const blocks = data.reportBlocks || [];
  const selectedStudents = students.filter((student) => selected.has(student.id));

  function toggle(id) {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function selectAll() { setSelected(new Set(students.map((student) => student.id))); }
  function clearSelection() { setSelected(new Set()); }

  function gerarBlocoPdf(list = selectedStudents, nome = blockName) {
    if (!list.length) { setMessage('Selecione ao menos um aluno para gerar o bloco.'); return; }
    exportReportBlockPdf(buildReportBlock(data, list, { nome }));
  }
  function gerarBlocoExcel(list = selectedStudents, nome = blockName) {
    if (!list.length) { setMessage('Selecione ao menos um aluno para gerar o bloco.'); return; }
    exportReportBlockWorkbook(buildReportBlock(data, list, { nome }));
  }

  function salvarBloco() {
    if (!selectedStudents.length) { setMessage('Selecione ao menos um aluno antes de salvar o bloco.'); return; }
    const config = createReportBlockConfig({ nome: blockName, studentIds: selectedStudents.map((student) => student.id) });
    const next = { ...data, reportBlocks: [config, ...blocks] };
    setData(next);
    onSave?.(next);
    setMessage(`Bloco "${config.nome}" salvo com ${config.studentIds.length} aluno(s) para reuso.`);
  }

  function gerarBlocoSalvo(config, tipo) {
    const list = students.filter((student) => config.studentIds.includes(student.id));
    if (!list.length) { setMessage(`Nenhum aluno visível corresponde ao bloco "${config.nome}".`); return; }
    if (tipo === 'pdf') gerarBlocoPdf(list, config.nome); else gerarBlocoExcel(list, config.nome);
  }

  function excluirBloco(id) {
    const next = { ...data, reportBlocks: blocks.filter((config) => config.id !== id) };
    setData(next);
    onSave?.(next);
  }

  function exportarBloco(config) {
    downloadJson(`Bloco_Relatorio_${config.nome.replace(/\s+/g, '_')}.json`, serializeReportBlockConfig(config));
  }

  function importarBloco(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const config = parseReportBlockConfig(String(reader.result || '{}'));
        const next = { ...data, reportBlocks: [config, ...blocks] };
        setData(next);
        onSave?.(next);
        setMessage(`Bloco "${config.nome}" importado com ${config.studentIds.length} aluno(s).`);
      } catch (error) {
        setMessage(`Falha ao importar bloco: ${error.message || 'JSON inválido.'}`);
      }
    };
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  }

  return <>
    <header className='header'><div><p>relatórios</p><h1>Central de relatórios</h1><span>Gere relatórios individuais, monte blocos automáticos por seleção de alunos e exporte ou importe a configuração do bloco.</span></div></header>
    <div className='split reports-layout'>
      <section className='card'>
        <h2>Alunos visíveis</h2>
        <p className='hint'>{students.length} aluno(s). Selecione para compor um bloco automático.</p>
        <div className='actions'><button type='button' onClick={selectAll}>Selecionar todos</button><button type='button' onClick={clearSelection}>Limpar seleção</button></div>
        <div className='report-student-list'>
          {students.map((student) => <StudentReportRow key={student.id} student={student} report={reportsById[student.id]} selected={selected.has(student.id)} onToggle={toggle} data={data} user={user} />)}
          {!students.length && <p className='hint'>Nenhum aluno visível para este perfil.</p>}
        </div>
      </section>
      <section className='card'>
        <h2>Bloco automático</h2>
        <label>Nome do bloco<input value={blockName} onChange={(event) => setBlockName(event.target.value)} /></label>
        <p className='hint'>{selectedStudents.length} aluno(s) selecionado(s) para o bloco atual.</p>
        <div className='actions'>
          <button type='button' onClick={() => gerarBlocoPdf()}>Gerar bloco em PDF</button>
          <button type='button' onClick={() => gerarBlocoExcel()}>Gerar bloco em Excel</button>
          <button type='button' onClick={salvarBloco}>Salvar bloco para reuso</button>
        </div>
        {message && <p className='hint'>{message}</p>}
        <h3>Blocos salvos</h3>
        {!blocks.length && <p className='hint'>Nenhum bloco automático salvo ainda.</p>}
        <div className='list'>
          {blocks.map((config) => <article key={config.id} className='log'>
            <b>{config.nome}</b>
            <small>{config.studentIds.length} aluno(s) · criado em {formatDate(config.criadoEm)}</small>
            <div className='actions'>
              <button type='button' onClick={() => gerarBlocoSalvo(config, 'pdf')}>Gerar PDF</button>
              <button type='button' onClick={() => gerarBlocoSalvo(config, 'excel')}>Gerar Excel</button>
              <button type='button' onClick={() => exportarBloco(config)}>Exportar JSON</button>
              <button type='button' onClick={() => excluirBloco(config.id)}>Excluir</button>
            </div>
          </article>)}
        </div>
        <label className='file-button'>Importar bloco JSON<input type='file' accept='application/json,.json' onChange={importarBloco} /></label>
      </section>
    </div>
  </>;
}
